import { useState } from 'react';
import type { ProjectDocument } from '@faktorplus/shared';
import {
  useCompanyDocuments,
  useDeleteDocument,
  useDocumentCategories,
  useProjectDocuments,
  useUploadDocument,
} from '../../api/documents';
import { useVendors } from '../../api/dictionaries';
import { useCurrentUser } from '../../auth/auth-provider';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorBox, LoadingBlock, Panel } from '../../components/ui/feedback';
import { Field, Input, Select } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { ApiError, fetchDocumentObjectUrl } from '../../lib/api-client';
import { formatShortDate, plural } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './projects.module.css';

/**
 * The site's document folder (24.09.2026): contracts, protocols, hand-overs,
 * photos — filed under the site and, when it concerns one, under the trading
 * partner. Below it, the company templates every site pulls from.
 *
 * Deliberately no deadlines and no reminders — the client asked for an
 * ordered folder, and said so twice.
 */
export function ProjectDocuments({ projectId }: { projectId: string }) {
  const user = useCurrentUser();
  const [categoryId, setCategoryId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const { data: documents = [], isLoading, error } = useProjectDocuments(projectId, {
    categoryId: categoryId || undefined,
    vendorId: vendorId || undefined,
  });
  const { data: categories = [] } = useDocumentCategories();
  const { data: vendors = [] } = useVendors();
  const { data: templates = [] } = useCompanyDocuments();

  const hasFilter = categoryId !== '' || vendorId !== '';

  return (
    <>
      <Panel
        title={`Dokumenty budowy — ${documents.length} ${plural(documents.length, ['plik', 'pliki', 'plików'])}`}
        action={
          <div className={styles.panelActions}>
            <Select
              compact
              className={styles.statusFilter}
              aria-label="Kategoria dokumentu"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">Wszystkie kategorie</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
            <Select
              compact
              className={styles.statusFilter}
              aria-label="Kontrahent dokumentu"
              value={vendorId}
              onChange={(event) => setVendorId(event.target.value)}
            >
              <option value="">Wszyscy kontrahenci</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </Select>
            <Button variant="primary" onClick={() => setIsAdding(true)}>
              + Dodaj dokument
            </Button>
          </div>
        }
      >
        {error && <ErrorBox>Nie udało się pobrać dokumentów.</ErrorBox>}
        {isLoading && <LoadingBlock />}

        {!isLoading && !error && documents.length === 0 && (
          <EmptyState
            mark="∅"
            title="Brak dokumentów"
            description={
              hasFilter
                ? 'Nic nie pasuje do filtrów. Zmień je albo wyczyść.'
                : 'Protokoły, umowy i zdjęcia z tej budowy będą tutaj.'
            }
          />
        )}

        {documents.length > 0 && (
          <DocumentsTable documents={documents} canDelete={(doc) => mayDelete(user, doc)} />
        )}
      </Panel>

      <Panel title="Wzory firmowe">
        {templates.length === 0 ? (
          <p className={pageStyles.muted}>
            Brak wzorów. Biuro dodaje je w Ustawieniach → Dokumenty.
          </p>
        ) : (
          <DocumentsTable documents={templates} canDelete={() => false} />
        )}
      </Panel>

      {isAdding && (
        <UploadDocumentDialog
          projectId={projectId}
          onClose={() => setIsAdding(false)}
        />
      )}
    </>
  );
}

/** Office removes anything; a manager only what they added themselves. */
function mayDelete(
  user: { role: string; fullName: string },
  document: ProjectDocument,
): boolean {
  if (user.role === 'ADMIN' || user.role === 'ACCOUNTANT') return true;
  return document.uploadedByName === user.fullName;
}

export function DocumentsTable({
  documents,
  canDelete,
}: {
  documents: ProjectDocument[];
  canDelete: (document: ProjectDocument) => boolean;
}) {
  const remove = useDeleteDocument();

  return (
    <div className={pageStyles.tableWrap}>
      {remove.error && (
        <ErrorBox>
          {remove.error instanceof ApiError
            ? remove.error.message
            : 'Nie udało się usunąć dokumentu.'}
        </ErrorBox>
      )}
      <table className={pageStyles.table}>
        <thead>
          <tr>
            <th>Nazwa</th>
            <th>Kategoria</th>
            <th className={pageStyles.hideOnPhone}>Kontrahent</th>
            <th className={pageStyles.hideOnPhone}>Dodał</th>
            <th className={pageStyles.hideOnPhone}>Data</th>
            <th className={`${pageStyles.numeric} ${pageStyles.hideOnPhone}`}>Rozmiar</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {documents.map((document) => (
            <tr key={document.id}>
              <td className={pageStyles.strong}>{document.name}</td>
              <td className={pageStyles.muted}>{document.categoryName}</td>
              <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                {document.vendorName ?? '—'}
              </td>
              <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                {document.uploadedByName ?? '—'}
              </td>
              <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                {formatShortDate(document.uploadedAt)}
              </td>
              <td className={`${pageStyles.numeric} ${pageStyles.hideOnPhone}`}>
                {formatSize(document.sizeBytes)}
              </td>
              <td>
                <div className={pageStyles.rowActions}>
                  <OpenDocumentButton document={document} />
                  {canDelete(document) && (
                    <Button
                      variant="danger"
                      isLoading={remove.isPending && remove.variables === document.id}
                      onClick={() => remove.mutate(document.id)}
                    >
                      Usuń
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Documents open in a new tab off an authorised blob — a plain `<a href>`
 * would arrive at the API with no token. The object URL lives as long as the
 * tab; the browser reclaims it with the session.
 */
function OpenDocumentButton({ document }: { document: ProjectDocument }) {
  const [isOpening, setIsOpening] = useState(false);

  async function open() {
    setIsOpening(true);
    try {
      const url = await fetchDocumentObjectUrl(document.id);
      window.open(url, '_blank', 'noopener');
    } finally {
      setIsOpening(false);
    }
  }

  return (
    <Button variant="soft" isLoading={isOpening} onClick={() => void open()}>
      Otwórz
    </Button>
  );
}

export function UploadDocumentDialog({
  projectId,
  onClose,
}: {
  /** Null = a company template (settings screen). */
  projectId: string | null;
  onClose: () => void;
}) {
  const upload = useUploadDocument();
  const { data: categories = [] } = useDocumentCategories();
  const { data: vendors = [] } = useVendors();

  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  async function handleSubmit() {
    setValidationError(null);

    if (name.trim().length < 2) {
      setValidationError('Nadaj dokumentowi nazwę');
      return;
    }
    if (!categoryId) {
      setValidationError('Wybierz kategorię');
      return;
    }
    if (files.length === 0) {
      setValidationError('Wybierz plik');
      return;
    }

    // Several photos at once share the typed name, numbered — nobody names
    // fourteen shots of the same wall one by one.
    for (const [index, file] of files.entries()) {
      await upload.mutateAsync({
        projectId,
        name: files.length === 1 ? name.trim() : `${name.trim()} (${index + 1})`,
        categoryId,
        vendorId: vendorId || undefined,
        file,
      });
    }
    onClose();
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={projectId ? 'Nowy dokument budowy' : 'Nowy wzór firmowy'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={upload.isPending}>
            Anuluj
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button variant="primary" isLoading={upload.isPending} onClick={() => void handleSubmit()}>
            Dodaj
          </Button>
        </>
      }
    >
      <Field label="Nazwa" hint="Nazwy plików ze skanera nic nie mówią — nadaj własną.">
        {(id) => (
          <Input
            id={id}
            value={name}
            autoFocus
            placeholder="np. Protokół odbioru — parter"
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>

      <Field label="Kategoria">
        {(id) => (
          <Select id={id} value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            <option value="">— wybierz kategorię</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {projectId && (
        <Field
          label="Kontrahent"
          hint="Opcjonalnie — dokument będzie widoczny przy tym kontrahencie na budowie."
        >
          {(id) => (
            <Select id={id} value={vendorId} onChange={(event) => setVendorId(event.target.value)}>
              <option value="">— bez kontrahenta</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      <Field label="Plik" hint="PDF, zdjęcia lub dokumenty Office. Zdjęć można wybrać kilka naraz.">
        {(id) => (
          <Input
            id={id}
            type="file"
            multiple
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
            onChange={(event) => setFiles([...(event.target.files ?? [])])}
          />
        )}
      </Field>

      {validationError && <ErrorBox>{validationError}</ErrorBox>}
      {upload.error && (
        <ErrorBox>
          {upload.error instanceof ApiError ? upload.error.message : 'Nie udało się wgrać pliku.'}
        </ErrorBox>
      )}
    </Modal>
  );
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}
